<script lang="ts">
  // ⌘K: search pages, settings, clients and actions. Arrow keys move, Enter
  // runs, Escape closes (Modal).
  import { LayoutDashboard, Timer, ListChecks, Users, ChartPie, Settings, LifeBuoy, Plus, Play, SunMoon, Search, CornerDownLeft, UserPlus, Palette, Bell, Clock, LogOut } from '@lucide/svelte';
  import type { Component } from 'svelte';
  import Modal from '../ui/Modal.svelte';
  import Kbd from '../ui/Kbd.svelte';
  import { shell } from './shell.svelte';
  import { navigate } from '../router.svelte';
  import { t } from '../i18n.svelte';
  import { data } from '../stores/data.svelte';
  import { timer } from '../stores/timer.svelte';
  import { prefs } from '../prefs.svelte';
  import { session } from '../stores/session.svelte';
  import { entryEditor } from '../stores/editor.svelte';
  import { toast } from '../toast.svelte';
  import { errorMessage } from '../api';
  import { clientColor } from '../format';

  interface Item {
    id: string;
    group: string;
    label: string;
    icon?: Component<any>;
    color?: string;
    hint?: string;
    keywords?: string;
    run: () => void;
  }

  let query = $state('');
  let index = $state(0);
  let list: HTMLElement | null = $state(null);

  const items = $derived.by<Item[]>(() => {
    const pages: Item[] = [
      { id: 'p-dash', group: t('palette.pages'), label: t('nav.dashboard'), icon: LayoutDashboard, hint: 'G D', run: () => navigate('/app') },
      { id: 'p-timer', group: t('palette.pages'), label: t('nav.timer'), icon: Timer, hint: 'G T', run: () => navigate('/app/timer') },
      { id: 'p-entries', group: t('palette.pages'), label: t('nav.entries'), icon: ListChecks, hint: 'G R', run: () => navigate('/app/registraties') },
      { id: 'p-clients', group: t('palette.pages'), label: t('nav.clients'), icon: Users, hint: 'G K', run: () => navigate('/app/klanten') },
      { id: 'p-reports', group: t('palette.pages'), label: t('nav.reports'), icon: ChartPie, hint: 'G A', run: () => navigate('/app/rapporten') },
      { id: 'p-support', group: t('palette.pages'), label: t('nav.support'), icon: LifeBuoy, run: () => navigate('/app/support') }
    ];
    const actions: Item[] = [
      { id: 'a-new', group: t('palette.actions'), label: t('nav.new_entry'), icon: Plus, hint: 'N', run: () => entryEditor.open() },
      { id: 'a-client', group: t('palette.actions'), label: t('nav.new_client'), icon: UserPlus, run: () => navigate('/app/klanten?new=1') },
      { id: 'a-theme', group: t('palette.actions'), label: t('palette.toggle_theme'), icon: SunMoon, run: () => prefs.update({ theme: prefs.resolvedTheme === 'dark' ? 'light' : 'dark' }) },
      { id: 'a-logout', group: t('palette.actions'), label: t('nav.logout'), icon: LogOut, run: () => session.logout() }
    ];
    if (timer.running) {
      actions.unshift({ id: 'a-stop', group: t('palette.actions'), label: t('timer.stop'), icon: Timer, hint: 'S', run: () => timer.stop().catch((e) => toast.error(errorMessage(e))) });
    }
    const settings: Item[] = [
      { id: 's-account', group: t('palette.settings'), label: `${t('nav.settings')}: Account`, icon: Settings, keywords: 'profiel wachtwoord account password profile', run: () => navigate('/app/instellingen/account') },
      { id: 's-appearance', group: t('palette.settings'), label: `${t('nav.settings')}: ${t('shell.theme')}`, icon: Palette, keywords: 'weergave appearance kleur color dark donker', run: () => navigate('/app/instellingen/weergave') },
      { id: 's-work', group: t('palette.settings'), label: `${t('nav.settings')}: Werkweek & afronding`, icon: Clock, keywords: 'werkweek afronding rounding work week target doel', run: () => navigate('/app/instellingen/werk') },
      { id: 's-notif', group: t('palette.settings'), label: `${t('nav.settings')}: Meldingen`, icon: Bell, keywords: 'notifications meldingen push', run: () => navigate('/app/instellingen/meldingen') }
    ];
    const clients: Item[] = data.activeClients.flatMap((c) => [
      { id: `c-${c.id}`, group: t('palette.clients'), label: c.name, icon: Users, color: clientColor(c), keywords: `${c.contact_person || ''} ${c.email || ''}`, run: () => navigate(`/app/klanten/${c.id}`) },
      {
        id: `cs-${c.id}`,
        group: t('palette.clients'),
        label: t('palette.start_for', { name: c.name }),
        icon: Play,
        color: clientColor(c),
        keywords: `start timer ${c.name}`,
        run: () =>
          timer
            .start({ clientId: c.id, clientName: c.name, rate: c.default_rate })
            .then(() => navigate('/app/timer'))
            .catch((e) => toast.error(errorMessage(e)))
      }
    ]);
    return [...actions, ...pages, ...settings, ...clients];
  });

  const results = $derived.by(() => {
    const q = query.trim().toLocaleLowerCase();
    if (!q) return items.filter((i) => !i.id.startsWith('cs-')).slice(0, 18);
    const words = q.split(/\s+/);
    return items
      .map((i) => {
        const hay = `${i.label} ${i.keywords || ''} ${i.group}`.toLocaleLowerCase();
        if (!words.every((w) => hay.includes(w))) return null;
        const score = i.label.toLocaleLowerCase().startsWith(q) ? 0 : i.label.toLocaleLowerCase().includes(q) ? 1 : 2;
        return { i, score };
      })
      .filter((x): x is { i: Item; score: number } => !!x)
      .sort((a, b) => a.score - b.score)
      .map((x) => x.i)
      .slice(0, 30);
  });

  const grouped = $derived.by(() => {
    const groups: Array<{ name: string; items: Array<{ item: Item; idx: number }> }> = [];
    results.forEach((item, idx) => {
      let g = groups.find((x) => x.name === item.group);
      if (!g) groups.push((g = { name: item.group, items: [] }));
      g.items.push({ item, idx });
    });
    return groups;
  });

  $effect(() => {
    void query;
    index = 0;
  });
  $effect(() => {
    if (!shell.paletteOpen) query = '';
  });

  function run(item: Item | undefined) {
    if (!item) return;
    shell.paletteOpen = false;
    item.run();
  }

  function key(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      index = Math.min(results.length - 1, index + 1);
      list?.querySelector(`[data-idx="${index}"]`)?.scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      index = Math.max(0, index - 1);
      list?.querySelector(`[data-idx="${index}"]`)?.scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter') {
      event.preventDefault();
      run(results[index]);
    }
  }
</script>

<Modal bind:open={shell.paletteOpen} variant="bare" align="top" size="md" ariaLabel={t('nav.search')}>
  <div class="palette">
    <div class="input-row">
      <Search size={18} />
      <input placeholder={t('palette.placeholder')} bind:value={query} onkeydown={key} data-autofocus autocomplete="off" spellcheck="false" />
      <Kbd>esc</Kbd>
    </div>
    <div class="results" bind:this={list}>
      {#each grouped as group (group.name)}
        <div class="group eyebrow">{group.name}</div>
        {#each group.items as { item, idx } (item.id)}
          <button type="button" class="res" class:sel={idx === index} data-idx={idx} onpointermove={() => (index = idx)} onclick={() => run(item)}>
            {#if item.icon}<span class="ic" style:color={item.color}><item.icon size={16} /></span>{/if}
            <span class="lbl truncate">{item.label}</span>
            {#if item.hint}<Kbd>{item.hint}</Kbd>{/if}
            {#if idx === index}<span class="enter"><CornerDownLeft size={14} /></span>{/if}
          </button>
        {/each}
      {:else}
        <div class="empty">{t('palette.no_results')}</div>
      {/each}
    </div>
    <div class="foot">{t('palette.hint')}</div>
  </div>
</Modal>

<style>
  .palette { display: flex; flex-direction: column; max-height: 70vh; min-height: 0; }
  .input-row { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-3) var(--space-4); border-bottom: 1px solid var(--border); color: var(--text-3); }
  input { flex: 1; min-width: 0; border: 0; outline: none; background: transparent; font-size: var(--text-md); color: var(--text); }
  .results { overflow-y: auto; padding: var(--space-1_5); min-height: 0; }
  .group { padding: var(--space-2_5) var(--space-2_5) var(--space-1); }
  .res { display: flex; align-items: center; gap: var(--space-3); width: 100%; height: var(--control-h); padding: 0 var(--space-2_5); border: 0; border-radius: var(--radius-sm); background: transparent; color: var(--text); font-size: var(--text-md); text-align: left; }
  .res.sel { background: var(--brand-soft); }
  .ic { display: inline-grid; place-items: center; width: 26px; height: 26px; border-radius: var(--radius-xs); background: var(--surface-3); color: var(--text-2); flex-shrink: 0; }
  .lbl { flex: 1; }
  .enter { display: inline-flex; color: var(--brand); }
  .empty { padding: var(--space-8); text-align: center; color: var(--text-3); }
  .foot { border-top: 1px solid var(--border); padding: var(--space-2) var(--space-3); font-size: var(--text-2xs); color: var(--text-3); }
</style>
